import React from "react";
import EditIcon from "@mui/icons-material/Edit";
import { EmployeeAbsenceBalanceDto } from "../../../types/absence";
import { DataTable, Column, Action } from "../../../components/common/DataTable/DataTable";
import { Typography } from "@mui/material";
import type { SearchableColumn } from "../../../components/common/SearchInputs";

interface EmployeeBalancesListProps {
  balances: EmployeeAbsenceBalanceDto[];
  isLoading: boolean;
  onAdjustClick: (balance: EmployeeAbsenceBalanceDto) => void;
  totalCount?: number;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  isLoadingRows?: boolean;
  onLoadMore?: () => void;
  enableSearch?: boolean;
  searchableColumns?: SearchableColumn[];
  onSearchChange?: (filters: Record<string, any>) => void;
}

const EmployeeBalancesList: React.FC<EmployeeBalancesListProps> = ({
  balances,
  isLoading,
  onAdjustClick,
  totalCount,
  hasNextPage,
  isLoadingMore,
  isLoadingRows,
  onLoadMore,
  enableSearch = false,
  searchableColumns = [],
  onSearchChange,
}) => {
  const columns: Column[] = [
    {
      field: "employeeName",
      headerName: "Employee",
      flex: 2,
    },
    {
      field: "year",
      headerName: "Year",
      flex: 0.7,
      minWidth: 80,
    },
    {
      field: "vacationDaysAvailable",
      headerName: "Vacation Available",
      flex: 1,
      renderCell: (value: number) => (
        <Typography variant="body2" fontWeight={500} color="primary.main">
          {value}
        </Typography>
      ),
    },
    {
      field: "vacationDaysUsed",
      headerName: "Vacation Used",
      flex: 1,
      renderCell: (value: number) => (
        <Typography variant="body2">{value}</Typography>
      ),
    },
    {
      field: "vacationDaysCarryOver",
      headerName: "Carry Over",
      flex: 1,
      renderCell: (value: number) => (
        <Typography variant="body2" color="text.secondary">
          {value > 0 ? value : "-"}
        </Typography>
      ),
    },
    {
      field: "sickDaysAvailable",
      headerName: "Sick Days Available",
      flex: 1,
      renderCell: (value: number) => (
        <Typography variant="body2" fontWeight={500} color="warning.main">
          {value}
        </Typography>
      ),
    },
    {
      field: "sickDaysUsed",
      headerName: "Sick Days Used",
      flex: 1,
      renderCell: (value: number) => (
        <Typography variant="body2">{value}</Typography>
      ),
    },
    {
      field: "lastUpdatedAt",
      headerName: "Last Updated",
      flex: 1.5,
      renderCell: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  const getActions = (
    balance: EmployeeAbsenceBalanceDto
  ): Action<EmployeeAbsenceBalanceDto>[] => {
    return [
      {
        icon: <EditIcon />,
        label: "Adjust Balance",
        onClick: onAdjustClick,
        color: "primary",
      },
    ];
  };

  return (
    <DataTable
      items={balances}
      columns={columns}
      actions={getActions}
      isLoading={isLoading}
      totalCount={totalCount}
      hasNextPage={hasNextPage}
      isLoadingMore={isLoadingMore}
      isLoadingRows={isLoadingRows}
      onLoadMore={onLoadMore}
      enableSearch={enableSearch}
      searchableColumns={searchableColumns}
      onSearchChange={onSearchChange}
    />
  );
};

export default EmployeeBalancesList;






